import React from 'react';
import { createIconElement, stanAnalytics } from '@/lib';
import { TypingText } from './TypingText';
import AiGenerateProduct from '@/assets/icons/ic-generate-product.svg';
import IcAiDocument from '@/assets/icons/ic-ai-document.svg';
import OnboardingStanley from '@/assets/icons/onboarding-stanley.svg';
import { useAskStanleyStore } from '@/stores/askStanleyStore';
import { useUser } from '@/queries/useUser';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { EXPERIMENTS } from '@/features/Experiments/constants';
import { useExperiments } from '@/features/Experiments/hooks';

interface QuickChatPromptsProps {
    onSendMessage: (message: string) => void;
    showPrompts: boolean;
}

export const QuickChatPrompts: React.FC<QuickChatPromptsProps> = ({ onSendMessage, showPrompts }) => {
    const isOpen = useAskStanleyStore(state => state.isOpen);
    const { user } = useUser();
    const isMobile = useIsMobile();
    const { hasExperiment } = useExperiments();
    const hasDynamicDashboardExperiment = !!hasExperiment(EXPERIMENTS.DYNAMIC_DASHBOARD_V8);
    if (!showPrompts) return null;

    return (
        <>
            {/* Greeting Prompt */}
            <div className='mb-4 flex flex-col gap-2'>
                <div className='text-brand-soft flex size-6 items-center justify-center'>
                    {createIconElement(OnboardingStanley, { className: 'size-6' })}
                </div>
                <h4 className='heading-h4'>
                    {isOpen && (
                        <TypingText
                            text='How can I help?'
                            speed={40}
                            className='from-brand-dark to-brand-main bg-gradient-to-r bg-clip-text text-transparent'
                        />
                    )}
                </h4>
            </div>

            {/* Quick Action Buttons */}
            <div className='mb-4 overflow-x-auto'>
                <div className='flex gap-2 whitespace-nowrap'>
                    {hasDynamicDashboardExperiment && (
                        <button
                            onClick={() => {
                                stanAnalytics('chat-v2-create-a-product-clicked', {
                                    meta: { user_id: user?.user_id, username: user?.username },
                                    props: { platform: isMobile ? 'mobile' : 'desktop' },
                                });
                                onSendMessage('Can you give me some product ideas?');
                            }}
                            className='bg-brand-subtle hover:bg-brand-soft flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-base font-semibold transition-colors'
                        >
                            <div className='from-brand-dark to-brand-main flex size-5 items-center justify-center rounded bg-gradient-to-br'>
                                {createIconElement(AiGenerateProduct, { className: 'size-5 text-canvas' })}
                            </div>
                            Create a Product
                        </button>
                    )}
                    <button
                        onClick={() => {
                            stanAnalytics('chat-v2-create-a-marketing-post-clicked', {
                                meta: { user_id: user?.user_id, username: user?.username },
                                props: { platform: isMobile ? 'mobile' : 'desktop' },
                            });
                            onSendMessage('Make me a marketing post');
                        }}
                        className='bg-brand-subtle hover:bg-brand-soft flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-base font-semibold transition-colors'
                    >
                        <div className='bg-info-main flex size-5 items-center justify-center rounded'>
                            {createIconElement(IcAiDocument, { className: 'size-5 text-canvas' })}
                        </div>
                        Create a Marketing Post
                    </button>
                </div>
            </div>
        </>
    );
};
